import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import type { FileRejection } from 'react-dropzone'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { Upload } from 'lucide-react'
import { Button } from '../ui/Button'

interface UploadZoneProps {
  onFileAccepted: (file: File) => void
  /** Format / privacy note shown beneath the drop box. */
  subtext?: string
  /** Highlights the zone red when the upload is required but not yet done. */
  required_error?: boolean
}

export function UploadZone({ onFileAccepted, subtext, required_error = false }: UploadZoneProps) {
  const reducedMotion = useReducedMotion()
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null)
      if (rejectedFiles.length > 0) {
        const firstError = rejectedFiles[0].errors[0]
        if (firstError.code === 'file-too-large') {
          setError('File too large. Maximum size is 10 MB.')
        } else if (firstError.code === 'file-invalid-type') {
          setError('Unsupported format. Please upload a PDF, PNG, or JPEG file.')
        } else {
          setError('Could not accept this file. Please try another.')
        }
        return
      }
      // The 50-page PDF cap is enforced server-side (the mock has no PDF parser).
      // When the backend rejects an over-length PDF the surfaced copy is:
      // "This PDF has more than 50 pages. Please split it and upload again."
      if (acceptedFiles.length > 0) {
        onFileAccepted(acceptedFiles[0])
      }
    },
    [onFileAccepted]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    noClick: true,   // We handle click via the Browse button
    noKeyboard: false,
  })

  // Use plain div for dropzone root (avoids Framer Motion event handler conflicts)
  // and put scale animation on a wrapping motion.div
  const rootProps = getRootProps()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      <div style={{ width: '100%' }}>
        <div
          {...rootProps}
          style={{
            background: required_error ? 'var(--color-error-surface)' : 'var(--color-surface-base)',
            border: required_error
              ? '1px solid var(--color-error-tint-10)' /* faint red border — Figma error upload (node 379:131) */
              : '1px solid var(--color-outline-subtle)',
            borderRadius: 'var(--radius-xl)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-4)',
            padding: 'var(--spacing-8) var(--spacing-8) var(--spacing-4)',
            cursor: 'pointer',
            outline: 'none',
          }}
          role="button"
          aria-label={`Upload area. Drag and drop a file or press Enter to browse. ${subtext ?? 'Accepted formats: PDF, PNG, JPEG. Maximum 10 MB; PDFs up to 50 pages.'}`}
          tabIndex={0}
          onClick={() => open()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              open()
            }
          }}
        >
          <input {...getInputProps()} aria-hidden="true" />

          {/* Inner white drop box — icon and prompt sit side by side */}
          <motion.div
            animate={
              reducedMotion ? {} : isDragActive ? { scale: 1.02 } : { scale: 1 }
            }
            transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: [0.0, 0.0, 0.2, 1.0] }}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: required_error
                ? 'var(--color-error-surface)'
                : 'var(--color-surface-container-lowest)',
              border: required_error
                ? '1px dashed var(--color-error-border)'
                : isDragActive
                  ? '1px dashed var(--color-action-primary)'
                  : '1px solid var(--color-outline-subtle)',
              boxShadow: isDragActive
                ? required_error
                  ? '0px 1px 6.5px var(--color-error-tint-20)' /* red glow — error state */
                  : 'var(--shadow-focus-ring)'
                : 'none',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-4)',
              padding: 'var(--spacing-10) var(--spacing-6)',
              transition: reducedMotion
                ? 'border-color 0.05s, box-shadow 0.05s'
                : 'border-color 0.2s ease-out, box-shadow 0.2s ease-out',
            }}
          >
            {/* Upload icon in a rounded square */}
            <motion.div
              animate={
                reducedMotion ? {} : isDragActive ? { scale: 1.08 } : { scale: 1 }
              }
              transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: [0.0, 0.0, 0.2, 1.0] }}
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-md)',
                background: required_error
                  ? 'var(--color-error-tint-20)' /* error icon tile — Figma error upload (node 379:137) */
                  : 'var(--color-info-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transformOrigin: 'right center',
              }}
            >
              <Upload
                size={26}
                color={required_error ? 'var(--color-text-primary)' : 'var(--color-action-primary)'}
                strokeWidth={1.5}
                aria-hidden="true"
              />
            </motion.div>

            {/* Drag / browse prompt */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--spacing-2)',
                flexWrap: 'wrap',
              }}
            >
              {isDragActive ? (
                <motion.span
                  initial={{ scale: 1 }}
                  animate={reducedMotion ? { scale: 1 } : { scale: 1.08 }}
                  transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: [0.0, 0.0, 0.2, 1.0] }}
                  style={{
                    display: 'inline-block',
                    transformOrigin: 'left center',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-body-md)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--color-text-primary)',
                    lineHeight: 'var(--leading-normal)',
                  }}
                >
                  Drop your file here
                </motion.span>
              ) : (
                <>
                  <span
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-body-md)',
                      fontWeight: 'var(--weight-semibold)',
                      color: 'var(--color-text-primary)',
                      lineHeight: 'var(--leading-normal)',
                    }}
                  >
                    Drag and drop your file
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-body-sm)',
                      fontWeight: 'var(--weight-regular)',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 'var(--leading-normal)',
                    }}
                  >
                    or
                  </span>
                  <Button
                    variant="link"
                    onClick={(e) => {
                      e.stopPropagation()
                      open()
                    }}
                    style={{ fontSize: 'var(--text-body-md)' }}
                  >
                    Browse files
                  </Button>
                </>
              )}
            </div>
          </motion.div>

          {/* Format / privacy note */}
          {subtext && (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                fontWeight: 'var(--weight-regular)',
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {subtext}
            </p>
          )}
        </div>
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={reducedMotion ? { duration: 0.2 } : { duration: 0.2, ease: [0.0, 0.0, 0.2, 1.0] }}
            role="alert"
            style={{
              margin: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-error-border)',
              padding: '0 var(--spacing-3)',
            }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}
